import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { XIcon, HeartIcon, SendIcon } from 'lucide-react'
import { StringItem } from '../App'
import { addComment, likeString, unlikeString } from '../api/strings'
import {
  consumeSessionExpiredFlag,
  isLoggedIn,
} from '../api/auth'
import { formatStringTimestamp } from '../utils/format'
import {
  isSessionExpiredMessage,
  redirectIfSessionExpired,
  redirectToLoginForExpiredSession as goToLoginForExpiredSession,
} from '../utils/sessionAuth'
import { invalidate, useString, useComments } from '../api/hooks'

interface StringDetailModalProps {
  string: StringItem
  onClose: () => void
  onNavigateToProfile?: (username: string) => void
}

const DEFAULT_AVATAR_SRC = '/red_eye.png'

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const sheetVariants = {
  hidden: { y: '100%', opacity: 0.6 },
  visible: { y: 0, opacity: 1 },
  exit: { y: '100%', opacity: 0 },
}

export function StringDetailModal({
  string,
  onClose,
  onNavigateToProfile,
}: StringDetailModalProps) {
  const navigate = useNavigate()
  const [authorImageBroken, setAuthorImageBroken] = useState(false)
  const [draft, setDraft] = useState('')
  const [actionError, setActionError] = useState('')
  const [sending, setSending] = useState(false)
  const [liking, setLiking] = useState(false)
  // Optimistic like override: null means "use server value"
  const [likeOverride, setLikeOverride] = useState<{ liked: boolean; count: number } | null>(null)

  const stringId = string.id

  const { data: stringData, error: stringErr, isLoading: stringLoading } = useString(stringId)
  const { data: commentsData, error: commentsErr, isLoading: commentsLoading, mutate: mutateComments } = useComments(stringId)

  const loading = stringLoading || commentsLoading
  const loadError = stringErr instanceof Error ? stringErr.message : commentsErr instanceof Error ? commentsErr.message : ''

  // Derive liked/likeCount from server data, with optimistic local override
  const liked = likeOverride !== null ? likeOverride.liked : Boolean(stringData?.likedByMe)
  const likeCount = likeOverride !== null ? likeOverride.count : (stringData?.stats?.likeCount ?? null)
  const authorProfileImageUrl = stringData?.authorProfileImageUrl ?? string.authorProfileImageUrl ?? null

  const comments = useMemo(
    () =>
      (commentsData ?? []).map((c) => {
        const { time } = formatStringTimestamp(c.createdAt)
        return { id: c.commentId, author: `@${c.authorUsername}`, text: c.text, time: time || '' }
      }),
    [commentsData],
  )

  useEffect(() => {
    setAuthorImageBroken(false)
  }, [stringId])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const redirectToLoginForExpiredSession = useCallback(() => {
    goToLoginForExpiredSession(navigate, { onClose })
  }, [navigate, onClose])

  useEffect(() => {
    if (isSessionExpiredMessage(loadError)) {
      redirectToLoginForExpiredSession()
    }
  }, [loadError, redirectToLoginForExpiredSession])

  const handleLike = () => {
    if (!isLoggedIn()) {
      if (consumeSessionExpiredFlag()) {
        redirectToLoginForExpiredSession()
        return
      }
      setActionError('Log in to like strings.')
      return
    }
    if (liking) return
    setActionError('')
    setLiking(true)

    const base = typeof likeCount === 'number' ? likeCount : 0
    const nextLiked = !liked
    setLikeOverride({ liked: nextLiked, count: Math.max(0, base + (nextLiked ? 1 : -1)) })

    const op = nextLiked ? likeString : unlikeString
    op(stringId)
      .then((updated) => {
        setLikeOverride({ liked: Boolean(updated.likedByMe), count: updated.stats?.likeCount ?? 0 })
        invalidate.string(stringId)
        invalidate.feed()
      })
      .catch((err) => {
        setLikeOverride({ liked: !nextLiked, count: Math.max(0, base + (!nextLiked ? 1 : -1)) })
        const message = err instanceof Error ? err.message : 'Could not update like.'
        if (redirectIfSessionExpired(navigate, err, { onClose })) return
        setActionError(message)
      })
      .finally(() => setLiking(false))
  }

  const handleSend = async () => {
    const trimmed = draft.trim()
    if (!trimmed || sending) return
    if (!isLoggedIn()) {
      if (consumeSessionExpiredFlag()) {
        redirectToLoginForExpiredSession()
        return
      }
      setActionError('Log in to comment.')
      return
    }
    setActionError('')
    setSending(true)
    try {
      const created = await addComment(stringId, trimmed)
      // Optimistically append to cached comments without revalidating
      mutateComments(
        (prev) => [
          ...(prev ?? []),
          { ...created, commentId: created.commentId },
        ],
        { revalidate: false },
      )
      invalidate.comments(stringId)
      setDraft('')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not post comment.'
      if (redirectIfSessionExpired(navigate, err, { onClose })) return
      setActionError(message)
    } finally {
      setSending(false)
    }
  }

  const handleAuthorClick = () => {
    if (!onNavigateToProfile) return
    onClose()
    onNavigateToProfile(string.authorUsername || string.author)
  }

  const authorAvatarSrc =
    authorProfileImageUrl && !authorImageBroken
      ? authorProfileImageUrl
      : DEFAULT_AVATAR_SRC

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col justify-end"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.22 }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        className="relative z-10 w-full max-w-lg mx-auto bg-[#0d0d0d]/95 backdrop-blur-2xl border border-white/10 rounded-t-3xl flex flex-col max-h-[88vh]"
        variants={sheetVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 340, damping: 38, mass: 0.9 }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 transition-colors"
        >
          <XIcon className="w-5 h-5 text-white/70" strokeWidth={2} />
        </button>

        {(loadError || actionError) && (
          <div className="px-6 pt-5">
            <p className="font-mono text-xs text-rose-300/90">{loadError || actionError}</p>
          </div>
        )}

        {/* Scrollable area: string content + comments */}
        <div className="flex-1 overflow-y-auto min-h-0">
        {/* String content */}
        <div className="px-6 pt-4 pb-6 border-b border-white/10">
          <p className="font-mono text-lg md:text-xl text-white leading-relaxed whitespace-pre-wrap tracking-wide">
            {string.content}
          </p>
          <div className="flex items-center justify-between gap-4 mt-4">
            <button
              type="button"
              onClick={handleAuthorClick}
              className="min-w-0 flex items-center gap-2.5 text-left group"
            >
              <span className="w-8 h-8 rounded-full overflow-hidden border border-white/20 bg-white/5 shrink-0">
                <img
                  src={authorAvatarSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={() => setAuthorImageBroken(true)}
                />
              </span>
              <span className="min-w-0 font-mono text-xs text-white/50 tracking-widest uppercase group-hover:text-white/80 transition-colors">
                <span className="truncate inline-block max-w-[8rem] align-bottom">
                  {string.author}
                </span>
                {` · ${string.time}${string.date ? ` · ${string.date}` : ''}`}
              </span>
            </button>
            <motion.button
              onClick={handleLike}
              className="flex items-center space-x-1.5 group"
              whileTap={{ scale: 0.82 }}
              transition={{ type: 'spring', stiffness: 500, damping: 22 }}
              disabled={liking}
            >
              <motion.div
                animate={liked ? { scale: [1, 1.4, 1] } : { scale: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <HeartIcon
                  className={`w-5 h-5 transition-colors ${liked ? 'text-rose-400 fill-rose-400' : 'text-white/40 group-hover:text-white/70'}`}
                />
              </motion.div>
              <span className="font-mono text-xs text-white/50 tracking-widest tabular-nums">
                {typeof likeCount === 'number' ? likeCount : '—'}
              </span>
            </motion.button>
          </div>
        </div>

        {/* Comments section */}
        <div className="px-6 py-4 space-y-5">
          {loading && (
            <p className="font-mono text-xs text-white/30 text-center tracking-widest uppercase pt-6">
              Loading…
            </p>
          )}
          {comments.length === 0 && (
            <p className="font-mono text-xs text-white/30 text-center tracking-widest uppercase pt-6">
              No echoes yet. Be first.
            </p>
          )}
          {comments.map((c) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col space-y-1"
            >
              <div className="flex items-center space-x-2">
                <span className="font-mono text-xs text-white/80 tracking-widest uppercase">
                  {c.author}
                </span>
                <span className="font-mono text-[10px] text-white/30 tracking-widest">
                  {c.time}
                </span>
              </div>
              <p className="font-mono text-sm text-white/70 leading-relaxed">
                {c.text}
              </p>
            </motion.div>
          ))}
        </div>
        </div>

        {/* Comment input */}
        <div className="px-4 py-3 border-t border-white/10 flex items-center space-x-2 shrink-0">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Add an echo..."
            className="flex-1 bg-white border border-white/25 rounded-full px-4 py-2 font-mono text-xs text-black placeholder-black/40 focus:outline-none focus:border-white/45 focus:ring-1 focus:ring-white/25 transition-all"
          />
          <motion.button
            onClick={handleSend}
            whileTap={{ scale: 0.88 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 transition-colors disabled:opacity-30"
            disabled={!draft.trim() || sending}
          >
            <SendIcon className="w-4 h-4 text-white/70" />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  )
}
